import * as _ from 'lodash';

import { Component, OnInit } from '@angular/core';
import { ConfigurationType, IConfiguration, IOrganisation, IPlatform, WelcomePageConfig } from '@bciti/types';
import { ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { ConfigurationService } from 'src/app/services/configuration/configuration.service';
import { IBreadcrumbItem } from 'src/app/interfaces/breadcrumb/breadcrumb.interface';
import { Module } from '@bciti/types';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-organisation-update',
    templateUrl: './organisation-update.component.html',
    styleUrls: ['./organisation-update.component.scss'],
})
export class OrganisationUpdateComponent implements OnInit {
    private destroy$ = new Subject();
    private update$ = new ReplaySubject();

    orgTag: string;
    organisation: IOrganisation;
    platform: IPlatform;
    loading = false;
    orgSaveLoading = false;

    breadcrumb: IBreadcrumbItem[] = [
        {
            link: '/organisations',
            title: 'Organisation',
        },
    ];

    constructor(private route: ActivatedRoute, private organisationService: OrganisationService, private configurationService: ConfigurationService, private toast: ToastrService) {}

    ngOnInit(): void {
        this.route.params.subscribe((param) => {
            if (param.tag) {
                this.orgTag = param.tag;

                this.breadcrumb.push({
                    link: '/organisations',
                    title: this.orgTag,
                });

                this.update$
                    .pipe(
                        takeUntil(this.destroy$),
                        tap(() => (this.loading = true)),
                        switchMap(() =>
                            combineLatest([this.organisationService.getOrganisationByTag(param.tag), this.configurationService.getConfigurationByKey(this.orgTag, ConfigurationType.PLATFORM)]).pipe(
                                takeUntil(this.destroy$),
                                map(([organisation, platform]) => {
                                    this.platform = platform;
                                    this.organisation = organisation;
                                    this.loading = false;
                                }),
                            ),
                        ),
                    )
                    .subscribe();

                this.update$.next();
            }
        });
    }

    get pageTitle() {
        return `Organisation : ${this.orgTag}`;
    }

    updateOrganisation(organisation: Partial<IOrganisation>) {
        this.orgSaveLoading = true;
        this.organisationService
            .updateOrganisation(this.orgTag, organisation)
            .pipe(
                takeUntil(this.destroy$),
                take(1),
                tap((response) => {
                    this.toast.success(`<b>${this.orgTag}</b> organisation was successfully updated`, 'Success', { enableHtml: true });
                    this.orgSaveLoading = false;
                }),
                catchError((error) => {
                    console.error(error);
                    this.toast.error(`Error updating <b>${this.orgTag}</b> organisation. See console for details`, 'Error', { enableHtml: true });
                    this.orgSaveLoading = false;
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    updatePlatform(platform: Partial<IPlatform>) {
        if (this.platform.support === null) {
            this.platform.support = {
                cards: [],
            };
        }

        this.platform = {
            ...this.platform,
            name: platform.name,
            modules: platform.modules,
            lang: platform.lang,
            consultation: platform?.consultation,
            family: platform?.family,
            cardPrint: {
                ...this.platform.cardPrint,
                canUserPrintCitizenCard: platform?.cardPrint.canUserPrintCitizenCard,
            },
            frenchLanguageCharterBanner: platform?.frenchLanguageCharterBanner,
            emailChannelAutoSubscription: platform?.emailChannelAutoSubscription,
            profileOptions: platform?.profileOptions,
            dashboardUrl: platform?.dashboardUrl,
        };

        const payload = {
            configuration: {
                key: ConfigurationType.PLATFORM,
                data: this.platform,
            },
        };

        this.loading = true;

        if (!payload.configuration.data.modules.includes(Module.CONSULTATION)) {
            payload.configuration.data.consultation = null;
        }

        this.configurationService
            .patchConfigurations(this.orgTag, payload)
            .pipe(
                takeUntil(this.destroy$),
                tap((configuration) => {
                    this.platform = configuration.data;
                    this.toast.success(`Platform Config <b>${this.orgTag}</b> was updated`, 'Success', { enableHtml: true });
                }),
                tap(() => (this.loading = false)),
                catchError((error) => {
                    this.toast.error('An error occured. View browser console for details', 'Error');
                    console.log('error', error);
                    this.loading = false;
                    return of();
                }),
            )
            .subscribe();
    }

    updateWelcomePage(welcomePage: WelcomePageConfig) {
        this.platform.welcomePage = welcomePage;

        this.updatePlatform(this.platform);
    }
}
